import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext.js';

const useDebounce = (value, delay) => {
    const [debouncedValue, setDebouncedValue] = useState(value);
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);
        return () => clearTimeout(handler);
    }, [value, delay]);
    return debouncedValue;
};

function ManageMembers() {
    const { token, user: adminUser } = useAuth();
    const navigate = useNavigate();
    const [users, setUsers] = useState([]);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const debouncedSearchTerm = useDebounce(searchTerm, 500);
    const isInitialMount = useRef(true);

    const fetchUsers = useCallback(async (currentPage, search) => {
        setIsLoading(true);
        try {
            const params = new URLSearchParams({ page: currentPage, search: search });
            const response = await fetch(`https://healthcard.searchisend.com/health_api/get_all_users_admin.php?${params.toString()}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.message);
            setUsers(data.users);
            setTotalPages(data.totalPages);
        } catch (error) {
            setMessage(`Error: ${error.message}`);
        } finally {
            setIsLoading(false);
        }
    }, [token]);

    useEffect(() => {
        setPage(1);
        fetchUsers(1, debouncedSearchTerm);
    }, [debouncedSearchTerm, fetchUsers]);

    useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false;
            return;
        }
        fetchUsers(page, debouncedSearchTerm);
    }, [page, fetchUsers, debouncedSearchTerm]);

    const handlePageChange = (newPage) => {
        if (newPage > 0 && newPage <= totalPages) {
            setPage(newPage);
        }
    };
    
    const handleDelete = async (userId) => {
        if (window.confirm('Are you sure you want to permanently delete this member? This action cannot be undone.')) {
            setMessage('Deleting member...');
            try {
                const response = await fetch(`https://healthcard.searchisend.com/health_api/delete_user_admin.php?id=${userId}`, {
                    method: 'DELETE',
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                const result = await response.json();
                if (!response.ok) throw new Error(result.message);
                setMessage('Member deleted successfully.');
                // Refresh the list of users to show the change
                fetchUsers(page, debouncedSearchTerm); 
            } catch (error) {
                setMessage(`Error: ${error.message}`);
            }
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h2 className="text-2xl font-semibold text-gray-700">Manage All Members</h2>
                {adminUser?.role === 'superadmin' && (
                    <Link to="/portal-management-7a8b9c/add-member" className="bg-brand-primary text-white font-bold py-2 px-4 rounded hover:bg-red-900 transition-colors">
                        + Add New Member
                    </Link>
                )}
            </div>
            
            {message && <p className="text-center text-blue-600">{message}</p>}

            <input
                type="text"
                placeholder="Search by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full p-2 border rounded-md"
            />
            <div className={`overflow-x-auto transition-opacity duration-300 ${isLoading ? 'opacity-50' : 'opacity-100'}`}>
                <table className="min-w-full bg-white border">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase">Image</th>
                            <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                            <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase">Email / Phone</th>
                            <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                            <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                            <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y">
                        {users.map(user => (
                            <tr key={user.id}>
                                <td className="py-2 px-4">
                                    <img src={user.profilePictureUrl ? `https://healthcard.searchisend.com${user.profilePictureUrl}` : `https://ui-avatars.com/api/?name=${user.firstName}+${user.lastName}&background=8b0000&color=fff&size=40`} alt="Profile" className="w-10 h-10 rounded-full object-cover" />
                                </td>
                                <td className="py-2 px-4 whitespace-nowrap">{user.firstName} {user.lastName}</td>
                                <td className="py-2 px-4 text-sm">{user.email || user.phoneNumber}</td>
                                <td className="py-2 px-4">{user.role}</td>
                                <td className="py-2 px-4">
                                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${user.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                                        {user.status}
                                    </span>
                                </td>
                                <td className="py-2 px-4 whitespace-nowrap">
                                    <button onClick={() => navigate(`/portal-management-7a8b9c/edit-user/${user.id}`)} className="bg-blue-500 text-white font-bold py-1 px-3 rounded mr-2">Edit</button>
                                    {adminUser?.role === 'superadmin' && user.id !== adminUser.id && (
                                        <button onClick={() => handleDelete(user.id)} className="bg-red-500 text-white font-bold py-1 px-3 rounded">Delete</button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
             <div className="mt-4 flex justify-between items-center">
                <button onClick={() => handlePageChange(page - 1)} disabled={page <= 1 || isLoading} className="px-4 py-2 bg-gray-300 rounded disabled:opacity-50">Previous</button>
                <span>Page {page} of {totalPages}</span>
                <button onClick={() => handlePageChange(page + 1)} disabled={page >= totalPages || isLoading} className="px-4 py-2 bg-gray-300 rounded disabled:opacity-50">Next</button>
            </div>
        </div>
    );
}

export default ManageMembers;

